import Image from "next/image";
import type { CSSProperties } from "react";

import { GROQImage } from "@/sanity/lib/definitions";

import { cn } from "@/lib/utils";

import { RankGleam } from "./RankGleam";
import { RankProgressArc, type RingTone } from "./RankReveal";

import ringFrame from "@/public/leaderboard/ranks/ring-frame.svg";
import laurel from "@/public/leaderboard/ranks/laurel.svg";

function RingTrack({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 220 220" fill="none" aria-hidden className={className}>
      <circle cx="110" cy="110" r="103.77" stroke="#fff" strokeOpacity="0.1" strokeWidth="12.2535" />
      <circle cx="110" cy="110" r="99.05" stroke="#fff" strokeOpacity="0.2" strokeWidth="0.9167" />
    </svg>
  );
}

export type RankArt = {
  scale: number;
  offsetY: number;
  gradient: string;
  gleam: string;
  ring: RingTone;
};

export const RANK_ART: RankArt[] = [
  {
    scale: 176.8,
    offsetY: 19.1,
    gradient: "linear-gradient(180deg, #2a2f32 0%, #51453d 85.227%)",
    gleam: "255 219 180", // bronze
    ring: "bronze",
  },
  {
    scale: 137,
    offsetY: 12.3,
    gradient: "linear-gradient(180deg, #222b2f 0%, #3f4446 85.795%)",
    gleam: "255 254 247", // graphite, near-achromatic
    ring: "silver",
  },
  {
    scale: 241,
    offsetY: 26.7,
    gradient: "linear-gradient(180deg, #212a2e 0%, #5a503b 82.67%)",
    gleam: "255 231 205", // marble with gold veining
    ring: "gold",
  },
  {
    scale: 108,
    offsetY: 0,
    gradient: "linear-gradient(180deg, #2a2f32 0%, #51453d 85%)",
    gleam: "255 235 179", // gold
    ring: "brightGold",
  },
];

const DRIFT_PERIODS = ["15s", "18s", "13s", "20s"];

export function RankAvatar({
  art,
  portrait,
  selected = false,
  seed = 0,
  className,
}: {
  art: RankArt;
  portrait?: GROQImage;
  selected?: boolean;
  seed?: number;
  className?: string;
}) {
  const driftPeriod = DRIFT_PERIODS[seed % DRIFT_PERIODS.length];

  const drift = Math.min(2, ((art.scale - 100) / 2) * 0.4);

  return (
    <div className={cn("relative aspect-square", className)}>
      <div
        className={cn(
          "absolute left-1/2 top-1/2 aspect-square -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full",
          "transition-[width] duration-500 ease-out",
          selected ? "w-[80%]" : "w-[87.7%]",
        )}
        style={{ background: art.gradient }}
      >
        {portrait?.src && (
          <div
            className="absolute inset-0 animate-rank-drift"
            style={
              {
                "--drift": `${drift}%`,
                "--drift-duration": driftPeriod,
              } as CSSProperties
            }
          >
            <Image
              src={portrait.src}
              alt=""
              width={portrait.width}
              height={portrait.height}
              className="absolute left-1/2 top-1/2 max-w-none"
              style={{
                width: `${art.scale}%`,
                height: "auto",
                transform: `translate(-50%, ${art.offsetY - 50}%)`,
              }}
            />
          </div>
        )}

        {portrait?.src && <RankGleam gleam={art.gleam} />}
      </div>

      <Image
        src={ringFrame}
        alt=""
        fill
        className={cn(
          "pointer-events-none object-contain transition-opacity duration-500 ease-out",
          selected ? "opacity-0" : "opacity-100",
        )}
      />
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 transition-opacity duration-500 ease-out",
          selected ? "opacity-100" : "opacity-0",
        )}
      >
        <RingTrack className="absolute inset-0 h-full w-full" />
        <RankProgressArc tone={art.ring} play={selected} />
      </div>
    </div>
  );
}

export function RewardsDivider({
  ornate,
  filled = true,
  label,
  className,
}: {
  ornate?: boolean;
  filled?: boolean;
  label?: string;
  className?: string;
}) {
  return (
    <div className={cn("flex w-full shrink-0 items-center justify-center", className)}>
      <span className="h-px min-w-0 flex-1 bg-white/10" />
      <div
        className={cn(
          "flex shrink-0 items-center gap-[0.5rem] rounded-[4rem] border border-white/10 px-[1.5rem] py-[0.5rem]",
          filled && "bg-[rgba(22,39,51,0.09)] backdrop-blur-[0.48rem]",
        )}
      >
        {ornate && <Image src={laurel} alt="" className="h-[2rem] w-auto shrink-0" />}
        <span className="whitespace-nowrap font-geist text-[1.75rem] font-medium leading-[1.5] text-white opacity-70">
          {label}
        </span>
        {ornate && <Image src={laurel} alt="" className="h-[2rem] w-auto shrink-0 -scale-x-100" />}
      </div>
      <span className="h-px min-w-0 flex-1 bg-white/10" />
    </div>
  );
}

export function RewardRow({
  icon,
  label,
  value,
}: {
  icon?: GROQImage;
  label?: string;
  value?: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-[1rem]">
      <span className="flex h-[3.25rem] w-[3.25rem] shrink-0 items-center justify-center rounded-[0.5rem] bg-white/[0.15] backdrop-blur-[0.2625rem]">
        {icon?.src && (
          <Image src={icon.src} alt="" width={icon.width} height={icon.height} className="h-[1.75rem] w-auto" />
        )}
      </span>
      <p className="m-0 min-w-0 font-geist text-[2rem] font-medium leading-[1.5] text-white">
        {label}
        {value ? ` (${value})` : ""}
      </p>
    </div>
  );
}
